/** @jsxImportSource @emotion/react */
import { ReactElement, useEffect, useState } from 'react';
import { ROUTES } from 'core/routes';
import { Route, Routes } from 'react-router-dom';
import { PrivateRoute } from 'core/PrivateRoute';
import { Dashboard } from 'pages/Dashboard';
import { VehiclesSearchPage } from 'pages/VehiclesSearch';
import { VehicleDetails } from 'pages/VehicleDetails';
import { AdminPage } from 'pages/AdminPage';
import { Login } from 'components/Login';
import { SigninOidc } from 'pages/SigninOidc';
import { SignoutOidc } from 'pages/SignoutOidc';
import { loadUserFromStorage } from 'core/auth/userService';
import { useAppSelector } from 'core/store';
import { Menu } from 'components/Menu';
import styled from '@emotion/styled';
import { MainHeader } from 'components/MainHeader';
import { useUser } from 'pages/hooks';
import { UserAccounts } from 'pages/UserAccounts';
import { UserAccountCreate, UserAccountEdit } from 'pages/UserAccountDetails';
import { LoadingScreen } from 'components/LoadingScreen';
import { RepairsSearch } from 'pages/RepairsSearch';
import { RedirectToStartPageHandler } from 'pages/RedirectToStartPageHandler';
import { DataAccessProfiles } from 'pages/DataAccessProfiles';
import {
  DataAccessProfileCreate,
  DataAccessProfileEdit,
} from 'pages/DataAccessProfilesDetails';
import { Reports } from 'pages/Reports';
import { ManualUpdate } from 'pages/ManualUpdate';
import { Companies } from 'pages/Companies';
import { DtlSearchPage } from 'pages/DtlSearch';
import { AccountProfiles } from 'pages/AccountProfiles';
import {
  AccountProfileCreate,
  AccountProfileEdit,
} from 'pages/AccountProfileDetails';
import { Permit } from 'api/resources/models/AutoGenerated';
import { CompanyCreate, CompanyEdit } from 'pages/CompaniesDetails';
import { ReportsDetails } from 'pages/ReportsDetails';
import { SilentSigninOidc } from 'pages/SilentSigninOidc';
import { CycleTime } from 'pages/CycleTime';
import { ErrorPage }  from 'pages/ErrorPage';

export default function Main(): ReactElement {
  const { isImpersonating, isLoading } = useAppSelector((state) => state.auth);
  const { user, error } = useUser();
  const [open, setOpen] = useState(false);
  let errorMessage = '';

  useEffect(() => {
    loadUserFromStorage();
  }, []);
  //capture any errors during user loading from interceptor
  errorMessage = error?.response?.data.Message;

  if(errorMessage) {
    return <ErrorPage errorMessage={errorMessage}/>
  }
  return isLoading ? (
    <div></div>
  ) : (
    <Container>
      {(!user || isImpersonating) && <LoadingScreen />}
      <Menu userPermits={user?.userPermits} />
      {user?.userPermits && (
        <MainHeader
          isOpen={open}
          userPermits={user.userPermits}
          setOpen={() => setOpen(!open)}
          currentUserFullName={user.fullName}
          currentUserUserName={user.userName}
        />
      )}
      <MainSection>
        <Routes>
          <Route element={<PrivateRoute />}>
            <Route
              path={ROUTES.redirectHandler}
              element={<RedirectToStartPageHandler />}
            />
            <Route path={ROUTES.dashboard} element={<Dashboard />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.CORE_VEHICLES]} />}
          >
            <Route
              path={ROUTES.vehiclesSearch}
              element={<VehiclesSearchPage />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.CORE_TOTALLOSS]} />}
          >
            <Route path={ROUTES.dtlDetails} element={<VehicleDetails />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.CORE_VEHICLES]} />}
          >
            <Route
              path={ROUTES.vehiclesSearchDetails}
              element={<VehicleDetails />}
            />
          </Route>

          <Route element={<PrivateRoute anyOfPermits={[Permit.CORE_SEARCH]} />}>
            <Route
              path={ROUTES.repairsSearchDetails}
              element={<VehicleDetails />}
            />
          </Route>

          <Route
            element={
              <PrivateRoute anyOfPermits={[Permit.ALLOW_MANUAL_UPDATES]} />
            }
          >
            <Route path={ROUTES.manualUpdate} element={<ManualUpdate />} />
          </Route>

          <Route element={<PrivateRoute anyOfPermits={[Permit.CORE_SEARCH]} />}>
            <Route path={ROUTES.repairsSearch} element={<RepairsSearch />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.CORE_CYCLETIME_V2]} />}
          >
            <Route path={ROUTES.cycleTimeSummary} element={<CycleTime />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.CORE_TOTALLOSS]} />}
          >
            <Route path={ROUTES.dtlSearch} element={<DtlSearchPage />} />
          </Route>

          <Route element={<PrivateRoute anyOfPermits={[Permit.CORE_ADMIN]} />}>
            <Route path={ROUTES.admin} element={<AdminPage />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route path={ROUTES.userAccounts} element={<UserAccounts />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.userAccountEdit}
              element={<UserAccountEdit />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.userAccountCreate}
              element={<UserAccountCreate />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.dataAccessProfiles}
              element={<DataAccessProfiles />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.dataAccessProfileEdit}
              element={<DataAccessProfileEdit />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.dataAccessProfilesDetailsCreate}
              element={<DataAccessProfileCreate />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.accountProfiles}
              element={<AccountProfiles />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.accountProfileCreate}
              element={<AccountProfileCreate />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route
              path={ROUTES.accountProfileEdit}
              element={<AccountProfileEdit />}
            />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.CORE_REPORTS]} />}
          >
            <Route path={ROUTES.reports} element={<Reports />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.CORE_REPORTS]} />}
          >
            <Route path={ROUTES.reportViewer} element={<ReportsDetails />} />
          </Route>
          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route path={ROUTES.companies} element={<Companies />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route path={ROUTES.companiesEdit} element={<CompanyEdit />} />
          </Route>

          <Route
            element={<PrivateRoute anyOfPermits={[Permit.GLOBAL_ADMIN]} />}
          >
            <Route path={ROUTES.companiesCreate} element={<CompanyCreate />} />
          </Route>

          <Route path={ROUTES.login} element={<Login />} />
          <Route path={ROUTES.signoutOidc} element={<SignoutOidc />} />
          <Route path={ROUTES.singinOidc} element={<SigninOidc />} />
          <Route
            path={ROUTES.silentSigninOidc}
            element={<SilentSigninOidc />}
          />
          <Route
            path={ROUTES.notFound}
            element={<div>Not Found</div>}
          />
        </Routes>
      </MainSection>
    </Container>
  );
}

const Container = styled('div')`
  width: 100%;
  height: 100%;
  padding-left: ${({ theme }) => `${theme.palette.drawer.width}px`};
  display: flex;
  flex-direction: column;
  @media (max-width: ${({ theme }) => `${theme.breakpoints.values.sm}px`}) {
    padding-left: 0;
  }
`;

const MainSection = styled.main`
  display: flex;
  flex-direction: column;
  height: calc(100% - 56px);
`;
